import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Select, Tag, Tooltip } from 'antd';
import {
  CloseOutlined,
  DeleteOutlined,
  FilterOutlined,
  PlusOutlined,
  SearchOutlined,
  SortAscendingOutlined,
} from '@ant-design/icons';
import type { FieldDefinition } from '@lens/shared';
import { FilterValueInput, OPERATOR_LABELS, getOperatorsForFieldType } from '../components/FilterRow';
import { SortAddPopover } from './SortAddPopover';
import { spacing } from '../theme/tokens';
import type { FieldLike, FilterLeaf, ReferenceOption, SortEntry } from './types';

const LIST_OPERATORS = ['in', 'not_in', 'contains_any', 'contains_all'];

/**
 * Значение условия после смены оператора. «В списке» хранит массив, «=» — одно
 * значение: без приведения «=» уезжал на сервер с массивом из фильтра колонки.
 */
function valueForOperator(op: string, value: unknown): unknown {
  if (op === 'is_empty' || op === 'is_not_empty') return undefined;
  if (LIST_OPERATORS.includes(op)) {
    if (Array.isArray(value)) return value;
    return value === undefined || value === null || value === '' ? [] : [value];
  }
  return Array.isArray(value) ? value[0] : value;
}

export interface FilterBarProps {
  fields: FieldLike[];
  filters: FilterLeaf[];
  onFiltersChange: (filters: FilterLeaf[]) => void;
  sorts: SortEntry[];
  onSortsChange: (sorts: SortEntry[]) => void;
  search: string;
  onSearchChange: (search: string) => void;
  ensureReferenceOptions?: (field: FieldLike) => Promise<ReferenceOption[]>;
  resolvedRefs?: Record<string, string>;
  extraButtons?: React.ReactNode;
  /** Имена для виртуальных полей сортировки (key → label). */
  sortNames?: Record<string, string>;
  /** Дополнительные ключи, под которые применяются операторы only is_empty/is_not_empty. */
  isVirtualSlug?: (slug: string) => boolean;
}

export function FilterBar({
  fields,
  filters,
  onFiltersChange,
  sorts,
  onSortsChange,
  search,
  onSearchChange,
  ensureReferenceOptions,
  resolvedRefs,
  extraButtons,
  sortNames,
  isVirtualSlug,
}: FilterBarProps): JSX.Element {
  const [showFilterEditor, setShowFilterEditor] = useState(false);
  const [showSortAdd, setShowSortAdd] = useState(false);

  useEffect(() => {
    if (filters.length > 0 && !showFilterEditor) {
      setShowFilterEditor(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (idx: number, patch: Partial<FilterLeaf>) => {
    onFiltersChange(filters.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };
  const removeFilter = (idx: number) => {
    onFiltersChange(filters.filter((_, i) => i !== idx));
  };
  const addEmptyFilter = () => {
    const firstField = fields[0];
    if (!firstField) return;
    const operators = isVirtualSlug?.(firstField.slug)
      ? ['is_empty', 'is_not_empty']
      : getOperatorsForFieldType(firstField.fieldType);
    onFiltersChange([
      ...filters,
      {
        fieldSlug: firstField.slug,
        operator: operators[0] ?? 'eq',
        value: undefined,
        ...(filters.length > 0 ? { combinator: 'and' as const } : {}),
      },
    ]);
    setShowFilterEditor(true);
  };
  const clearAllFilters = () => onFiltersChange([]);

  const addSort = (fieldSlug: string, direction: 'asc' | 'desc') => {
    onSortsChange([...sorts, { fieldSlug, direction }]);
    setShowSortAdd(false);
  };
  const removeSort = (idx: number) => {
    onSortsChange(sorts.filter((_, i) => i !== idx));
  };

  const hasFilters = filters.length > 0;
  const hasSorts = sorts.length > 0;

  return (
    <div style={{ marginBottom: spacing.md, display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
      <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Поиск..."
          size="small"
          autoComplete="off"
          style={{ width: 220 }}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          allowClear
          data-testid="table-search"
        />
        <Tooltip title={`Фильтр${hasFilters ? ` · ${filters.length}` : ''}`}>
          <Button
            size="small"
            icon={<FilterOutlined />}
            type={hasFilters ? 'primary' : 'default'}
            onClick={() => setShowFilterEditor((v) => !v)}
            data-testid="filter-toggle"
            aria-label="Фильтр"
          />
        </Tooltip>
        <Tooltip title={`Сортировка${hasSorts ? ` · ${sorts.length}` : ''}`}>
          <Button
            size="small"
            icon={<SortAscendingOutlined />}
            type={showSortAdd ? 'primary' : 'default'}
            onClick={() => setShowSortAdd((v) => !v)}
            aria-label="Сортировка"
          />
        </Tooltip>
        {extraButtons}
      </div>

      {hasSorts && (
        <div style={{ display: 'flex', gap: spacing.xs, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="lens-sort-label">
            <SortAscendingOutlined style={{ fontSize: 12 }} />
            Сортировка
          </span>
          {sorts.map((s, i) => (
            <Tag
              key={`s-${i}`}
              closable
              onClose={() => removeSort(i)}
              className="lens-sort-chip"
              data-testid={`sort-chip-${s.fieldSlug}`}
            >
              {sortNames?.[s.fieldSlug] ??
                fields.find((fd) => fd.slug === s.fieldSlug)?.name ??
                s.fieldSlug}{' '}
              {s.direction === 'asc' ? '↑' : '↓'}
            </Tag>
          ))}
        </div>
      )}

      {showFilterEditor && (
        <div className="lens-filter-editor" data-testid="filter-editor">
          <FilterEditor
            fields={fields}
            filters={filters}
            onUpdate={updateFilter}
            onRemove={removeFilter}
            onAdd={addEmptyFilter}
            onClear={clearAllFilters}
            onClose={() => setShowFilterEditor(false)}
            ensureReferenceOptions={ensureReferenceOptions}
            resolvedRefs={resolvedRefs}
            isVirtualSlug={isVirtualSlug}
          />
        </div>
      )}

      {showSortAdd && (
        <div className="lens-filter-editor">
          <SortAddPopover fields={fields} onAdd={addSort} onCancel={() => setShowSortAdd(false)} />
        </div>
      )}
    </div>
  );
}

export interface FilterEditorProps {
  fields: FieldLike[];
  filters: FilterLeaf[];
  onUpdate: (idx: number, patch: Partial<FilterLeaf>) => void;
  onRemove: (idx: number) => void;
  onAdd: () => void;
  onClear: () => void;
  onClose?: () => void;
  ensureReferenceOptions?: (field: FieldLike) => Promise<ReferenceOption[]>;
  resolvedRefs?: Record<string, string>;
  isVirtualSlug?: (slug: string) => boolean;
  /**
   * Компактный режим: без шапки «Фильтр / Очистить всё» и с узкими селектами —
   * для мест, где заголовок уже есть снаружи (окно быстрых фильтров).
   */
  compact?: boolean;
}

export function FilterEditor({
  fields,
  filters,
  onUpdate,
  onRemove,
  onAdd,
  onClear,
  onClose,
  ensureReferenceOptions,
  isVirtualSlug,
  compact,
}: FilterEditorProps): JSX.Element {
  const [refOptionsByField, setRefOptionsByField] = useState<Record<string, ReferenceOption[]>>({});

  const ensureRefForField = useCallback(
    async (field: FieldLike) => {
      if (refOptionsByField[field.slug]) return;
      if (!ensureReferenceOptions) return;
      const opts = await ensureReferenceOptions(field).catch(() => [] as ReferenceOption[]);
      setRefOptionsByField((prev) => ({ ...prev, [field.slug]: opts }));
    },
    [ensureReferenceOptions, refOptionsByField],
  );

  useEffect(() => {
    for (const f of filters) {
      const fd = fields.find((x) => x.slug === f.fieldSlug);
      if (fd?.fieldType === 'reference' || fd?.fieldType === 'user') void ensureRefForField(fd);
    }
  }, [filters, fields, ensureRefForField]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!compact && (filters.length > 0 || onClose) && (
        <div className="lens-filter-editor__head">
          <span className="lens-filter-editor__title">
            <FilterOutlined style={{ fontSize: 12 }} />
            Фильтр
            {filters.length > 0 && (
              <span className="lens-filter-editor__count">{filters.length}</span>
            )}
          </span>
          <div className="lens-filter-editor__head-actions">
            {filters.length > 0 && (
              <Button
                size="small"
                type="text"
                danger
                onClick={onClear}
                className="lens-filter-editor__clear"
              >
                Очистить всё
              </Button>
            )}
            {onClose && (
              <Tooltip
                title={
                  filters.length > 0
                    ? 'Свернуть редактор (фильтры останутся применёнными)'
                    : 'Скрыть редактор'
                }
              >
                <Button
                  size="small"
                  onClick={onClose}
                  icon={<CloseOutlined />}
                  className="lens-filter-editor__close"
                >
                  Скрыть
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      )}

      {filters.map((f, i) => {
        const fd = fields.find((x) => x.slug === f.fieldSlug) ?? null;
        const operators = isVirtualSlug?.(f.fieldSlug)
          ? ['is_empty', 'is_not_empty']
          : fd
            ? getOperatorsForFieldType(fd.fieldType)
            : ['eq', 'neq'];
        const noValue = ['is_empty', 'is_not_empty'].includes(f.operator);
        return (
          <div
            key={`fr-${i}`}
            className={`lens-filter-editor__row${compact ? ' lens-filter-editor__row--compact' : ''}`}
          >
            {i === 0 ? (
              <span className="lens-filter-editor__lead">где</span>
            ) : (
              <Select
                size="small"
                style={{ width: compact ? 64 : 72, flexShrink: 0 }}
                className="lens-filter-editor__combinator"
                value={f.combinator === 'or' ? 'or' : 'and'}
                onChange={(v) => onUpdate(i, { combinator: v as 'and' | 'or' })}
                options={[
                  { value: 'and', label: 'И' },
                  { value: 'or', label: 'ИЛИ' },
                ]}
                data-testid={`filter-combinator-${i}`}
              />
            )}
            <Select
              size="small"
              style={compact ? { flex: '0 1 200px', minWidth: 140 } : { flex: '0 1 220px', minWidth: 160 }}
              showSearch
              filterOption={(input, opt) =>
                (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
              value={f.fieldSlug}
              onChange={(slug) => {
                const newField = fields.find((x) => x.slug === slug);
                const ops = isVirtualSlug?.(slug)
                  ? ['is_empty', 'is_not_empty']
                  : newField
                    ? getOperatorsForFieldType(newField.fieldType)
                    : ['eq'];
                onUpdate(i, { fieldSlug: slug, operator: ops[0] ?? 'eq', value: undefined });
                if (newField?.fieldType === 'reference' || newField?.fieldType === 'user') void ensureRefForField(newField);
              }}
              options={fields.map((x) => ({ value: x.slug, label: x.name }))}
              placeholder="Поле"
              data-testid={`filter-field-${i}`}
            />
            <Select
              size="small"
              style={compact ? { flex: '0 1 140px', minWidth: 112 } : { flex: '0 1 140px', minWidth: 120 }}
              value={f.operator}
              onChange={(op) =>
                onUpdate(i, { operator: op, value: valueForOperator(op, f.value) })
              }
              options={operators.map((op) => ({ value: op, label: OPERATOR_LABELS[op] ?? op }))}
              data-testid={`filter-operator-${i}`}
            />
            {!noValue && (
              <FilterValueInput
                field={fd as FieldDefinition | null}
                operator={f.operator}
                value={f.value}
                onChange={(v) => onUpdate(i, { value: v })}
                refOptions={fd ? refOptionsByField[fd.slug] ?? [] : []}
                compact={compact}
              />
            )}
            <Button
              size="small"
              type="text"
              icon={<DeleteOutlined />}
              onClick={() => onRemove(i)}
              aria-label="Удалить условие"
              className="lens-filter-editor__remove"
            />
          </div>
        );
      })}

      <div>
        <Button
          size="small"
          type="dashed"
          icon={<PlusOutlined />}
          onClick={onAdd}
          className="lens-filter-editor__add"
        >
          Добавить условие
        </Button>
      </div>
    </div>
  );
}
